import { createContext, useContext, useState, useEffect } from "react";

// Use relative /api — same-origin avoids CORS on custom domains.
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      if (!sessionToken) {
        // No token at all — nothing to check
        return;
      }

      const headers = { 'Authorization': `Bearer ${sessionToken}` };

      const response = await fetch(
        `/api/auth/me`,
        {
          credentials: 'include',
          headers
        }
      );

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return;
      }

      // 🛡️ Only clear the token if the backend EXPLICITLY says the session
      // is invalid (401 Unauthorized). Any other status (500/502/503/504/520,
      // network hiccups, cold pod boots) is transient — keep the token so
      // the TV kiosk survives brief backend outages and reconnects itself.
      if (response.status === 401) {
        localStorage.removeItem('session_token');
      } else {
        console.warn(`Auth check got ${response.status} — keeping token, will retry.`);
      }
    } catch (error) {
      // Network / DNS / CORS failure = do NOT clear token. Keep session
      // sticky so the kiosk auto-heals when the pod comes back.
      console.warn('Auth check network error — keeping session token:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      await fetch(
        `/api/auth/logout`,
        { 
          method: 'POST',
          credentials: 'include',
          headers
        }
      );
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  const value = {
    user,
    setUser,
    loading,
    logout,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
